import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {findTimeZone} from '../../../../helpers/dateTimeHelpers';
import {
  InboxesSearchLocationsEnum,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
  TeamsFoldersEnum,
} from '../../../../models/url/inboxesUrlModel';
import {CellsContext} from '../../cells/context/cellsContext';
import {
  hasSearchInInboxesParameters,
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
  InboxesTeammateMessagesParameters,
  InboxesTeammateParameters,
  InboxesTeamParameters,
  InboxesUserParameters,
} from './inboxesParameters';
import {CellsSortModesEnum, findParametersCellsSortMode} from './inboxesParametersHelpers';

/*
 * Fetch path.
 */

/** Build the API url from inboxes parameters. */
export function buildParametersFetchPath(
  parameters: InboxesParameters,
  cellsContext?: CellsContext,
  canUseSearchRawEndpoint = false,
) {
  // If we have search parameters, use a different logic.
  if (hasSearchInInboxesParameters(parameters)) {
    return buildParametersSearchFetchPath(
      parameters,
      parameters.search,
      canUseSearchRawEndpoint ? 'search_raw' : 'search',
      cellsContext,
    );
  }

  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return `/inboxes/${parameters.inboxId}/conversations`;

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return `/tags/${parameters.viewId}/conversations`;

    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAM_LENS:
      return `/lenses/${parameters.lensId}/conversations`;

    case InboxesParametersTypesEnum.USER:
    case InboxesParametersTypesEnum.TEAMMATE:
      return `/team/${parameters.teammateId}/conversations/${parameters.folder}`;

    case InboxesParametersTypesEnum.TEAM:
      return `/teams/${parameters.teamId}/conversations`;

    case InboxesParametersTypesEnum.GLOBAL:
      return `/team/unified/conversations/assigned`;

    case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
      return buildTeammateMessagesParametersFetchPath(parameters);

    default:
      throw new UnreachableError(parameters);
  }
}

function buildTeammateMessagesParametersFetchPath(parameters: InboxesTeammateMessagesParameters) {
  switch (parameters.folder) {
    case TeammatesMessageFoldersEnum.DRAFTS:
    case TeammatesMessageFoldersEnum.SENT:
      return `/team/${parameters.teammateId}/messages/${parameters.folder}`;

    case TeammatesMessageFoldersEnum.OUTBOX:
      return undefined;

    default:
      throw new UnreachableError(parameters.folder);
  }
}

/*
 * Search fetch path.
 */

/** Build the full search query that includes modifiers that might be hidden. */
export function buildFullSearchQuery(
  parameters: InboxesConversationsParameters,
  searchParameters: InboxesSearchParameters,
  cellsContext?: CellsContext,
) {
  // If the search is global, there are no modifiers.
  const {location, query} = searchParameters;
  if (location === InboxesSearchLocationsEnum.GLOBAL) {
    return query;
  }

  // Otherwise, it depends on the parameters.
  const localModifiers = buildParametersSearchModifiers(
    {...parameters, search: searchParameters},
    cellsContext,
  );
  return `${localModifiers} ${query}`;
}

/** Build the API search url from inboxes parameters. */
export function buildParametersSearchFetchPath(
  parameters: InboxesConversationsParameters,
  searchParameters: InboxesSearchParameters,
  path: 'search' | 'search_autosuggest' | 'search_count' | 'search_hints' | 'search_raw' = 'search',
  cellsContext?: CellsContext,
) {
  const fullQuery = buildFullSearchQuery(parameters, searchParameters, cellsContext);
  return `/${path}/${encodeURIComponent(fullQuery)}`;
}

/** Build the API search suggestions url from inboxes parameters. */
export function buildParametersSearchSuggestionsFetchPath(
  parameters: InboxesConversationsParameters,
  searchParameters: InboxesSearchParameters,
  cellsContext?: CellsContext,
) {
  return buildParametersSearchFetchPath(parameters, searchParameters, 'search_autosuggest', cellsContext);
}

/** Build the API search count url from inboxes parameters. */
export function buildParametersSearchFetchTotalPath(
  parameters: InboxesConversationsParameters,
  searchParameters: InboxesSearchParameters,
  cellsContext?: CellsContext,
) {
  return buildParametersSearchFetchPath(parameters, searchParameters, 'search_count', cellsContext);
}

/** Build the API search suggestions url from inboxes parameters. */
export function buildParametersSearchHintsFetchPath(
  parameters: InboxesConversationsParameters,
  searchParameters: InboxesSearchParameters,
  cellsContext?: CellsContext,
) {
  return buildParametersSearchFetchPath(parameters, searchParameters, 'search_hints', cellsContext);
}

/** Build the search modifiers to scope the search to the specified parameters. */
export function buildParametersSearchModifiers(
  parameters: InboxesConversationsParameters,
  cellsContext?: CellsContext,
) {
  if (parameters.search?.location === InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE) {
    return `is:in-my-workspace`;
  }

  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return `inbox:${parameters.inboxId}`;

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return `tag:${parameters.viewId}`;

    case InboxesParametersTypesEnum.TEAM_LENS:
    case InboxesParametersTypesEnum.TEAMMATE_LENS:
      return `lens:${parameters.lensId}`;

    case InboxesParametersTypesEnum.USER: {
      // If is delegated, prepend team modifier with teammateId.
      if (cellsContext?.isDelegation) {
        return `team:${parameters.teammateId}`;
      }

      // Otherwise, return teammate-related modifiers.
      return buildParametersSearchTeammateModifiers(parameters);
    }

    case InboxesParametersTypesEnum.TEAMMATE:
      return buildParametersSearchTeammateModifiers(parameters);

    case InboxesParametersTypesEnum.TEAM:
      return buildParametersTeamSearchModifiers(parameters);

    case InboxesParametersTypesEnum.GLOBAL:
      return `is:assigned`;

    default:
      throw new UnreachableError(parameters);
  }
}

/** Build the search modifiers to scope the search to the specified teammate parameters. */
function buildParametersSearchTeammateModifiers(
  parameters: InboxesUserParameters | InboxesTeammateParameters,
) {
  switch (parameters.folder) {
    case TeammatesFoldersEnum.ASSIGNED:
    case TeammatesFoldersEnum.ASSIGNED_SHARED:
      return `assignee:${parameters.teammateId}`;

    case TeammatesFoldersEnum.SHARED_WITH_ME:
      return 'is:shared-with-me';

    case TeammatesFoldersEnum.DISCUSSIONS:
      return 'is:discussion';

    case TeammatesFoldersEnum.TRASHED:
      return 'is:tracking is:deleted';

    case TeammatesFoldersEnum.SPAM:
      return 'is:tracking is:spam';

    case TeammatesFoldersEnum.SENT:
      return 'author:me';

    default:
      return 'is:tracking';
  }
}

/** Build the search modifiers to scope the search to the specified team parameters. */
function buildParametersTeamSearchModifiers(parameters: InboxesTeamParameters) {
  const baseModifier = `team:${parameters.teamId}`;

  switch (parameters.folder) {
    case TeamsFoldersEnum.TRASHED:
      return `${baseModifier} is:deleted`;

    case TeamsFoldersEnum.SPAM:
      return `${baseModifier} is:spam`;

    default:
      return baseModifier;
  }
}

/*
 * Fetch query string.
 */

/** Build the context query parameter for search queries. */
export function buildParametersFetchQueryContext(parameters: InboxesParameters, cellsContext?: CellsContext) {
  // If this is not a search query, nothing to do.
  if (!hasSearchInInboxesParameters(parameters)) {
    return undefined;
  }

  // Otherwise, check what context we're in.
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAM:
    case InboxesParametersTypesEnum.GLOBAL:
    case InboxesParametersTypesEnum.TEAM_LENS:
    case InboxesParametersTypesEnum.TEAMMATE_LENS: // Teammate lenses consist of team inboxes.
      return 'team';

    case InboxesParametersTypesEnum.USER:
      return cellsContext?.isDelegation ? 'team' : 'teammate';

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
    case InboxesParametersTypesEnum.TEAMMATE:
      return 'teammate';

    default:
      throw new UnreachableError(parameters);
  }
}

/** Build the sort property query parameter. */
export function buildParametersFetchQuerySort(parameters: InboxesParameters) {
  switch (findParametersCellsSortMode(parameters)) {
    case CellsSortModesEnum.TRACKER:
      return 'date';

    default:
      return parameters.sortProperty;
  }
}

const timezone = findTimeZone();
export function buildParametersSearchTimezone(parameters: InboxesParameters) {
  // If we're not in search mode, no need to add timezone.
  if (!hasSearchInInboxesParameters(parameters)) {
    return undefined;
  }

  return timezone;
}
